import React, { useState, useEffect } from 'react';
import {
  Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Box, Typography, Divider, Avatar, IconButton, Tooltip,
  Popover, Card, CardContent, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Badge, Paper
} from '@mui/material';
import {
  Logout, GetApp, Public,
  Home, ManageAccounts, Euro, PrecisionManufacturing, AccountBalance,
  Assignment, LocalShipping, Assessment, ViewKanban, WhatsApp, Mail, Notifications,
  SmartToy, Language, Edit as EditIcon, Save as SaveIcon, CloudDownload,
  Inventory, Groups, Storefront,
  LightMode, DarkMode, Close as CloseIcon, Security as SecurityIcon
} from '@mui/icons-material';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import PrecisionManufacturingRoundedIcon from '@mui/icons-material/PrecisionManufacturingRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import SupervisorAccountRoundedIcon from '@mui/icons-material/SupervisorAccountRounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import StorageRoundedIcon from '@mui/icons-material/StorageRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../config/supabaseClient';
import UserCardPopover from './UserCardPopover';
import { API_BASE_URL } from '../config/api';
import AlpinorLogo from '../assets/images/ALPINOR_Schriftzug_Final.png';
import { getFilteredMenuStructure, getActiveSection } from '../config/menuConfig';
import jsPDF from 'jspdf';

const SIDEBAR_WIDTH = 250;

/* ── Design Tokens ── */
const BG_SIDEBAR = '#6B4F3A';
const BG_ITEM_HOVER = 'rgba(255, 255, 255, 0.05)';
const BG_ITEM_ACTIVE = 'rgba(0, 0, 0, 0.15)';
const TEXT_PRIMARY = '#F2E8D8';
const TEXT_MUTED = 'rgba(242, 232, 216, 0.7)';
const ACCENT = '#C9A227';
const BORDER = '#4a3525';

const sectionColors = {
  'Allgemein': '#3b82f6',
  'Verwaltung': '#ef4444',
  'Sortiment': '#8b5cf6',
  'CRM & Sales': '#10b981',
  'Produktion & Logistik': '#f59e0b',
  'Finanzen & Controlling': '#06b6d4',
  'Kanban & Aufgaben': '#d946ef',
  'Nachrichten': '#22c55e',
  'Administration': '#f43f5e',
};

const sectionIcons = {
  'BI & Management': <DashboardRoundedIcon />,
  'Workspace': <AssignmentRoundedIcon />,
  'CRM & Sales': <Storefront />,
  'Einkauf': <ShoppingCartRoundedIcon />,
  'Logistik & Lager': <Inventory2RoundedIcon />,
  'MES & Produktion': <PrecisionManufacturingRoundedIcon />,
  'Finanzen & Controlling': <AccountBalanceRoundedIcon />,
  'HR & Verwaltung': <SupervisorAccountRoundedIcon />,
  'Projektmanagement': <AccountTreeRoundedIcon />,
  'Stammdaten': <StorageRoundedIcon />,
  'Einstellungen': <SettingsRoundedIcon />,
  'Administration': <AdminPanelSettingsRoundedIcon />,
  'System-Handbuch': <AutoStoriesRoundedIcon />,
};

const IconTile = ({ title, icon, color, onClick, active = false, onHover, onLeave, dark = false }) => {
  const themeColor = color || (dark ? '#94a3b8' : '#64748b');
  const hoverBg = dark ? 'rgba(255,255,255,0.06)' : 'rgba(25, 118, 210, 0.04)';
  const activeBg = dark ? 'rgba(99,102,241,0.25)' : 'rgba(25, 118, 210, 0.08)';
  const accent = dark ? '#818cf8' : '#1976d2';

  return (
    <Box
      onClick={onClick}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        p: 0.8, borderRadius: 1.5, cursor: 'pointer', transition: 'all 0.2s',
        flex: 1, minWidth: 0,
        bgcolor: active ? activeBg : 'transparent',
        color: active ? accent : themeColor,
        '&:hover': {
          color: accent,
          bgcolor: hoverBg,
        },
      }}
    >
      <Box sx={{ height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {React.isValidElement(icon) && !icon.props.sx?.fontSize ? React.cloneElement(icon, { sx: { fontSize: 24 } }) : icon}
      </Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'inherit', lineHeight: 1, mt: 0.8, opacity: 0.9 }}>
        {title}
      </Typography>
    </Box>
  );
};

const Navigation = () => {
  const [user, setUser] = useState(null);
  const [orphanCount, setOrphanCount] = useState(0);
  const [orphanDetails, setOrphanDetails] = useState([]);
  const [datevIncomesCount, setDatevIncomesCount] = useState(0);
  const [datevExpensesCount, setDatevExpensesCount] = useState(0);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [overdueCount, setOverdueCount] = useState(0);
  const [overdueDetails, setOverdueDetails] = useState([]);
  const [incompleteEmployeesCount, setIncompleteEmployeesCount] = useState(0);
  const [incompleteEmployeesDetails, setIncompleteEmployeesDetails] = useState([]);
  const [mitarbeiterId, setMitarbeiterId] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [darkSidebar, setDarkSidebar] = useState(() => localStorage.getItem('sidebar_dark') === 'true');
  const [activeTenantId, setActiveTenantId] = useState(() => {
    try {
      const stored = localStorage.getItem('saas_active_tenant');
      return stored ? JSON.parse(stored).identifier : 'host';
    } catch {
      return 'host';
    }
  });

  useEffect(() => {
    const sync = () => {
      setDarkSidebar(localStorage.getItem('sidebar_dark') === 'true');
      try {
        const stored = localStorage.getItem('saas_active_tenant');
        setActiveTenantId(stored ? JSON.parse(stored).identifier : 'host');
      } catch (e) {
        setActiveTenantId('host');
      }
    };
    window.addEventListener('storage_theme_change', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('storage_theme_change', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  // Theme tokens (computed from darkSidebar)
  const BG = darkSidebar ? '#020617' : BG_SIDEBAR;
  const TEXT_PRI = darkSidebar ? '#ffffff' : TEXT_PRIMARY;
  const TEXT_MUT = darkSidebar ? '#94a3b8' : TEXT_MUTED;
  const HOVER_BG = darkSidebar ? 'rgba(255,255,255,0.04)' : BG_ITEM_HOVER;
  const ACTIVE_BG = darkSidebar ? 'rgba(99,102,241,0.2)' : BG_ITEM_ACTIVE;
  const BORDER_C = darkSidebar ? '#1e293b' : BORDER;
  const FOOT_BG = darkSidebar ? '#0a0a0a' : 'transparent';

  const navigate = useNavigate();
  const location = useLocation();
  const activeSection = getActiveSection(location.pathname);

  const [localProfile, setLocalProfile] = useState(() => {
    try { return JSON.parse(localStorage.getItem('local_profile') || '{}'); } catch { return {}; }
  });
  const [editForm, setEditForm] = useState({ name: '', initials: '' });

  useEffect(() => {
  }, [location.pathname, activeSection]);

  const fetchStats = async () => {
    try {
      const [orphRes, datevRes, overdueRes, chatRes, summaryRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/produktionen/orphans-count`),
        fetch(`${API_BASE_URL}/api/datev/pending`),
        fetch(`${API_BASE_URL}/api/kanban-tasks-overdue-count`),
        mitarbeiterId ? fetch(`${API_BASE_URL}/api/messages/unread-total/${mitarbeiterId}`) : Promise.resolve({ ok: false }),
        fetch(`${API_BASE_URL}/api/notifications/summary`)
      ]);

      let newOrphanCount = orphanCount;
      let newOverdueCount = overdueCount;
      let newIncompleteEmployeesCount = incompleteEmployeesCount;
      let newDatevIncome = datevIncomesCount;
      let newDatevExpense = datevExpensesCount;
      let newChatUnread = unreadChatCount;

      if (orphRes.ok) {
        const d = await orphRes.json();
        newOrphanCount = d.orphanCount || 0;
        setOrphanCount(newOrphanCount);
      }
      if (datevRes.ok) {
        const d = await datevRes.json();
        newDatevIncome = d.incomeCount || 0;
        newDatevExpense = d.expenseCount || 0;
        setDatevIncomesCount(newDatevIncome);
        setDatevExpensesCount(newDatevExpense);
      }
      if (overdueRes.ok) {
        const d = await overdueRes.json();
        newOverdueCount = d.overdueCount || 0;
        setOverdueCount(newOverdueCount);
      }
      if (chatRes.ok) {
        const d = await chatRes.json();
        newChatUnread = d.unreadCount || 0;
        setUnreadChatCount(newChatUnread);
      }
      if (summaryRes.ok) {
        const d = await summaryRes.json();
        newIncompleteEmployeesCount = d.incompleteEmployeesCount || 0;
        setIncompleteEmployeesCount(newIncompleteEmployeesCount);
      }

      window.dispatchEvent(new CustomEvent('navStatsUpdated', { 
        detail: { 
          notifCount: newOrphanCount + newOverdueCount + newIncompleteEmployeesCount,
          datevIncome: newDatevIncome,
          datevExpense: newDatevExpense,
          chatUnread: newChatUnread
        } 
      }));

    } catch (e) { console.error('Fetch stats failed', e); }
  };

  const fetchDetails = async () => {
    setIsLoadingDetails(true);
    try {
      const [orphDetailsRes, overdueDetailsRes, summaryRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/produktionen/orphans-details`),
        fetch(`${API_BASE_URL}/api/kanban-tasks-overdue-details`),
        fetch(`${API_BASE_URL}/api/notifications/summary`)
      ]);
      if (orphDetailsRes.ok) setOrphanDetails(await orphDetailsRes.json());
      if (overdueDetailsRes.ok) setOverdueDetails(await overdueDetailsRes.json());
      if (summaryRes.ok) {
        const d = await summaryRes.json();
        setIncompleteEmployeesDetails(d.incompleteEmployees || []);
      }
    } catch (e) { console.error('Fetch details failed', e); }
    finally { setIsLoadingDetails(false); }
  };

  useEffect(() => {
    const initUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const localProfile = JSON.parse(localStorage.getItem('local_profile') || '{}');
        
        let dbName = '';
        let pBild = '';
        let mDetails = null;
        try {
          const mRes = await fetch(`${API_BASE_URL}/api/mitarbeiter`);
          const mData = await mRes.json();
          const me = mData.find(m => m.email?.toLowerCase() === user.email.toLowerCase());
          if (me) {
            dbName = `${me.vorname} ${me.nachname}`.trim();
            pBild = me.profilbild || '';
            setMitarbeiterId(me._id);
            mDetails = me;
          }
        } catch (e) {
          console.error('Failed to init user from MongoDB:', e);
        }

        const metaName = user.user_metadata?.full_name;
        const { data: profile } = await supabase.from('profiles').select('full_name').eq('id', user.id).single();
        
        setUser({ 
          fullName: localProfile.name || dbName || profile?.full_name || metaName || user.email.split('@')[0], 
          email: user.email,
          profilbild: pBild,
          personalnummer: mDetails?.personalnummer || '',
          telefonnummer: mDetails?.telefonnummer || '',
          position: mDetails?.position || '',
          mitarbeiterDetails: mDetails
        });
      }
    };
    initUser();

    const handleSync = () => {
      initUser();
    };
    window.addEventListener('storage_theme_change', handleSync);
    return () => {
      window.removeEventListener('storage_theme_change', handleSync);
    };
  }, []);

  useEffect(() => {
    fetchStats();
    const timer = setInterval(fetchStats, 30000);
    return () => clearInterval(timer);
  }, [mitarbeiterId]);

  const handleNotifClick = (e) => {
    setNotifAnchor(e.currentTarget);
    fetchDetails();
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('isAuthenticated');
    navigate('/login');
  };

  const displayName = localProfile.name || user?.fullName || 'Unbekannt';
  const displayInitials = localProfile.initials || (displayName !== 'Unbekannt' ? displayName.charAt(0).toUpperCase() : 'U');

  const handleOpenEdit = () => { setEditForm({ name: displayName, initials: displayInitials }); setEditProfileOpen(true); };
  const handleSaveProfile = async () => {
    const updated = { name: editForm.name, initials: editForm.initials.toUpperCase().slice(0, 2) };
    setLocalProfile(updated);
    localStorage.setItem('local_profile', JSON.stringify(updated));
    setEditProfileOpen(false);

    try {
      const nameParts = editForm.name.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) {
        // 1. Update Supabase User Metadata
        await supabase.auth.updateUser({
          data: {
            full_name: editForm.name,
            first_name: firstName,
            last_name: lastName
          }
        });

        // 2. Update Supabase Profiles Table if available
        await supabase.from('profiles').update({ full_name: editForm.name }).eq('id', authUser.id);
        
        // 3. Sync to MongoDB Mitarbeiter Table!
        await fetch(`${API_BASE_URL}/api/employees/sync-onboarding`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: authUser.email,
            firstName: firstName,
            lastName: lastName,
            status: 'aktiv'
          })
        });
      }

      // Dispatch event to trigger LandingPage refresh
      window.dispatchEvent(new Event('storage_theme_change'));
    } catch (e) {
      console.error('Error saving profile changes:', e);
    }
  };

  return (
    <Drawer variant="permanent" sx={{
      width: SIDEBAR_WIDTH, flexShrink: 0,
      '& .MuiDrawer-paper': {
        width: SIDEBAR_WIDTH, bgcolor: BG, borderRight: `1px solid ${BORDER_C}`,
        display: 'flex', flexDirection: 'column', transition: 'background 0.3s',
        backgroundImage: darkSidebar ? 'none' : `linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(0,0,0,0.08) 100%), url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.08'/%3E%3C/svg%3E")`,
        backgroundBlendMode: 'overlay',
      }
    }}>
      <Box 
        sx={{ 
          py: 3, display: 'flex', justifyContent: 'center', cursor: 'pointer',
          '&:hover img': { transform: 'scale(1.15)' },
          '&:active img': { transform: 'scale(1.05)' },
        }} 
        onClick={() => navigate('/')}
      >
        <Box 
          component="img" 
          src={AlpinorLogo} 
          alt="Logo" 
          sx={{ 
            height: 'auto', 
            width: '100%',
            maxWidth: '200px',
            objectFit: 'contain',
            transition: 'transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }} 
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1 }}>
        <List disablePadding>
          {(() => {
            let lastCategory = null;
            return getFilteredMenuStructure().map((section) => {
              const isSectionActive = activeSection?.section === section.section;
              const sColor = sectionColors[section.section] || TEXT_PRI;
              const showCategory = section.category && section.category !== lastCategory;
              if (showCategory) lastCategory = section.category;

              return (
                <Box key={section.section} sx={{ mb: 0.5 }}>
                  {showCategory && (
                    <Typography sx={{ px: 3, pt: 1.5, pb: 0.5, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: ACCENT, letterSpacing: 0.5 }}>
                      {section.category}
                    </Typography>
                  )}
                  <ListItemButton
                    onClick={() => {
                      if (section.items && section.items.length > 0) {
                        navigate(section.items[0].route);
                      }
                    }}
                    sx={{
                      borderRadius: 2, py: 1, px: 2,
                      bgcolor: isSectionActive ? BG_ITEM_ACTIVE : 'transparent',
                      color: isSectionActive ? ACCENT : TEXT_PRI,
                      '&:hover': { bgcolor: HOVER_BG }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: isSectionActive ? ACCENT : TEXT_MUT }}>
                      {React.cloneElement(sectionIcons[section.section] || <Home />, { sx: { fontSize: 20 } })}
                    </ListItemIcon>
                    <ListItemText
                      primary={section.section}
                      primaryTypographyProps={{
                        fontSize: 13,
                        fontWeight: isSectionActive ? 700 : 400,
                        color: isSectionActive ? ACCENT : TEXT_PRI
                      }}
                    />
                  </ListItemButton>
                </Box>
              );
            });
          })()}
        </List>
      </Box>

      <Box sx={{ p: 2, bgcolor: FOOT_BG, borderTop: `1px solid ${BORDER_C}`, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <UserCardPopover user={user} onLogout={handleLogout}>
          <Tooltip title="Profil bearbeiten">
            <Avatar src={user?.profilbild} sx={{ bgcolor: BG_ITEM_ACTIVE, color: ACCENT, width: 80, height: 80, fontSize: 28, fontWeight: 700, mb: 1.5, cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: `2px solid ${ACCENT}` }}>{displayInitials}</Avatar>
          </Tooltip>
        </UserCardPopover>
        <Typography sx={{ fontWeight: 800, color: TEXT_PRI, fontSize: 15, textAlign: 'center', mb: 0.5 }} noWrap>{displayName}</Typography>
        <Typography sx={{ color: TEXT_MUT, fontSize: 12, textAlign: 'center', mb: 0.5 }} noWrap>{user?.email || '-'}</Typography>
        <Typography sx={{ color: TEXT_MUT, fontSize: 12, textAlign: 'center', mb: 2.5 }} noWrap>{user?.position || 'Keine Position'}</Typography>
        
        <Button 
          fullWidth 
          variant="outlined" 
          startIcon={<Logout />} 
          onClick={handleLogout}
          sx={{ 
            borderRadius: 2, 
            textTransform: 'none', 
            fontWeight: 700, 
            py: 1, 
            color: ACCENT, 
            borderColor: ACCENT, 
            '&:hover': { 
              borderColor: ACCENT, 
              bgcolor: 'rgba(201,162,39,0.1)' 
            } 
          }}
        >
          Abmelden
        </Button>
      </Box>

      {/* Popover for Notifications */}
      <Popover
        open={Boolean(notifAnchor)}
        anchorEl={notifAnchor}
        onClose={() => setNotifAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { borderRadius: 2, mt: -1, ml: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', border: `1px solid ${BORDER}` } }}
      >
        <Card sx={{ p: 0, minWidth: 260, maxWidth: 320 }}>
          <Box sx={{ p: 2, bgcolor: '#c9a227', borderBottom: `1px solid ${BORDER}` }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: TEXT_PRIMARY }}>Aktuelle Meldungen</Typography>
          </Box>
          <CardContent sx={{ p: 1, maxHeight: 400, overflowY: 'auto' }}>
            {isLoadingDetails ? (
              <Box sx={{ p: 2, textAlign: 'center' }}><Typography sx={{ fontSize: 12 }}>Lade Details...</Typography></Box>
            ) : (
              <>
                {overdueDetails.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#ef4444', px: 1, mb: 0.5, textTransform: 'uppercase' }}>Überfällige Aufgaben</Typography>
                    {overdueDetails.map(t => (
                      <Box key={t._id} onClick={() => { navigate('/kanban-aufgaben/kanban'); setNotifAnchor(null); }}
                        sx={{ p: 1, borderRadius: 1.5, '&:hover': { bgcolor: '#fef2f2' }, cursor: 'pointer', mb: 0.3 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: TEXT_PRIMARY }}>{t.title}</Typography>
                        <Typography sx={{ fontSize: 10, color: '#ef4444' }}>Fällig: {new Date(t.dueDate).toLocaleDateString('de-DE')}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                {orphanDetails.length > 0 && (
                  <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#ef4444', px: 1, mb: 0.5, textTransform: 'uppercase' }}>Fehlende Arbeitszeiten</Typography>
                    {orphanDetails.map(o => {
                      const prodName = o.produkte?.[0]?.name || o.produktkategorie || 'Unbekanntes Produkt';
                      return (
                        <Box key={o._id} onClick={() => { navigate('/produktion-logistik/produktion'); setNotifAnchor(null); }}
                          sx={{ p: 1, borderRadius: 1.5, '&:hover': { bgcolor: '#fef2f2' }, cursor: 'pointer', mb: 0.3 }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: TEXT_PRIMARY }}>Produktion: {prodName}</Typography>
                          <Typography sx={{ fontSize: 10, color: TEXT_MUTED }}>Charge: {o.chargenNummer || 'Unbekannt'} · {new Date(o.datum).toLocaleDateString('de-DE')}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
                {incompleteEmployeesDetails.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#f59e0b', px: 1, mb: 0.5, textTransform: 'uppercase' }}>Unvollständige Profile</Typography>
                    {incompleteEmployeesDetails.map(e => (
                      <Box key={e._id} onClick={() => { navigate('/verwaltung/mitarbeiter'); setNotifAnchor(null); }}
                        sx={{ p: 1, borderRadius: 1.5, '&:hover': { bgcolor: '#fffbeb' }, cursor: 'pointer', mb: 0.3 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: TEXT_PRIMARY }}>{e.name}</Typography>
                        <Typography sx={{ fontSize: 10, color: '#f59e0b' }}>Fehlt: {e.missing}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                {!(orphanDetails.length + overdueDetails.length + incompleteEmployeesDetails.length) && (
                  <Box sx={{ p: 2, textAlign: 'center' }}><Typography sx={{ fontSize: 12, color: TEXT_MUTED }}>Keine neuen Meldungen</Typography></Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Popover>

      <Dialog open={editProfileOpen} onClose={() => setEditProfileOpen(false)}>
        <DialogTitle>Profil bearbeiten</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Name" fullWidth value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
          <TextField label="Initialen" fullWidth value={editForm.initials} inputProps={{ maxLength: 2 }} onChange={e => setEditForm({ ...editForm, initials: e.target.value })} />
        </DialogContent>
        <DialogActions><Button onClick={() => setEditProfileOpen(false)}>Abbrechen</Button><Button onClick={handleSaveProfile} variant="contained">Speichern</Button></DialogActions>
      </Dialog>

      {/* Export Dialog Skeleton */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 900, display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#eff6ff', color: '#3b82f6', display: 'flex' }}><GetApp /></Box>
            Daten-Export & Berichte
          </Box>
          <IconButton onClick={() => setExportDialogOpen(false)} size="small" sx={{ bgcolor: '#f1f5f9' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4, bgcolor: '#f8fafc' }}>
          <Typography variant="body1" sx={{ color: '#475569', mb: 4, fontWeight: 500 }}>
            Wählen Sie aus, welche Datenbereiche Sie als CSV oder Excel exportieren möchten. Die hier angezeigten Bereiche richten sich nach Ihrem gebuchten Software-Paket.
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 4, maxHeight: '65vh', overflowY: 'auto', p: 1 }}>
            {getFilteredMenuStructure()
              .filter(s => s.section !== 'Administration' && s.section !== 'System-Handbuch')
              .map(section => (
                <Paper key={section.section} elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                   <Typography sx={{ fontWeight: 800, color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1, mb: 2 }}>
                     {section.section}
                   </Typography>
                   <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                     {section.items.map(item => (
                       <Box key={item.title} sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', transition: 'all 0.2s', '&:hover': { borderColor: '#3b82f6', bgcolor: '#eff6ff', transform: 'translateX(4px)' } }}>
                         <GetApp sx={{ color: '#3b82f6', fontSize: 18 }} />
                         <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>{item.title}</Typography>
                       </Box>
                     ))}
                   </Box>
                </Paper>
              ))}
              
              <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                 <Typography sx={{ fontWeight: 800, color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1, mb: 2 }}>
                   Sicherheit & System
                 </Typography>
                 <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                   <Box sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', transition: 'all 0.2s', '&:hover': { borderColor: '#f43f5e', bgcolor: '#fff1f2', transform: 'translateX(4px)' } }}>
                     <SecurityIcon sx={{ color: '#f43f5e', fontSize: 18 }} />
                     <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>Audit-Logs (Compliance)</Typography>
                   </Box>
                 </Box>
              </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setExportDialogOpen(false)} sx={{ fontWeight: 700, color: '#64748b', mr: 'auto' }}>Abbrechen</Button>
          <Button variant="contained" disabled sx={{ fontWeight: 800, borderRadius: 2, px: 4, py: 1 }}>Export Starten</Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default Navigation;